import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { TransportType } from "@boxem/core-api-schema";

import { updateBatchV2 } from "@/api/fulfillment";
import { formatValueAsCurrency } from "@/utils/formatUtils";
import { FbaBatch, useShipmentContext } from "@/contexts/ShipmentContext";
import Tabs from "@/components/Tabs/Tabs";
import DatePicker from "@/components/forms/DatePicker";
import Button from "@/components/forms/Button";
import Tooltip from "@/components/Tooltip";
import IconInfo from "@/components/icons/IconInfo";
import IconCopyClipboard from "@/components/icons/IconCopyClipboard";
import PageContent from "@/components/PageContent";
import IconCheckCircle from "@/components/icons/IconCheckCircle";
import ConfirmModal from "@/components/ConfirmModal";

import styles from "./ConfirmShipmentsPage.module.css";

import { FbaPageProps } from "../FbaShipmentFlow";
import PageHeader from "../PageHeader";
import PlacementOption from "./PlacementOption";
import ShipmentOption from "./ShipmentOption";
import { LabelsModal, LabelsModalItem } from "@/components/LabelsModal/LabelsModal";
import IconPrintGoogle from "@/components/icons/IconPrintGoogle";
import Flex from "@/components/Flex/Flex";

const placementOptionNotice = `
Shipping your inventory to more locations will generally minimize inbound placement service fees and
improve delivery speed to buyers. To receive reduced fees ensure that you complete all shipments and
do not delete, abandon, or misroute any shipment splits. Your shipping carrier costs are additional
and are not included in the inbound placement service fee.
`;

const readyToShipNotice = `
The ready-to-ship date is the earliest day at which you are ready to send out your shipments. It must
be in the future. This date can impact your transportation charges, and changing it now will cause
all the transportation fees to be re-fetched from Amazon, which may take a few minutes.
`;

export default function ConfirmShipmentsPage(props: FbaPageProps) {
  const {
    continueButton,
    onContinueClicked,
    onCancelClicked,
    cloneShipment,
  } = props;

  const { currentBatch, setCurrentBatch } = useShipmentContext();
  const [selectedShipmentTabIndex, setSelectedShipmentTabIndex] = useState(0);
  const [readyToShip, setReadyToShip] = useState(currentBatch?.readyToShip);
  const [readyToShipDateErrorMessage, setReadyToShipDateErrorMessage] = useState(null);
  const [confirmUpdateReadyToShipDateModalOpen, setConfirmUpdateReadyToShipDateModalOpen] = useState(false);
  const [labelsModalItems, setLabelsModalItems] = useState<LabelsModalItem[]>([]);

  const selectedPlacementOption = currentBatch.placementOptions.find(
    placementOption => placementOption.placementOptionId === currentBatch.selectedPlacementOptionId
  );

  const optPlacement = currentBatch.fees[selectedPlacementOption?.placementOptionId]?.placement === 0;

  useEffect(() => {
    if (!currentBatch.selectedPlacementOptionId && currentBatch?.placementOptions) {
      // select the first placement option as a default (if we don't have one selected):
      currentBatch.selectedPlacementOptionId = currentBatch.placementOptions?.[0]?.placementOptionId;
      setCurrentBatch({...currentBatch});
    }
    if (!!currentBatch) {
      setReadyToShip(currentBatch.readyToShip);
    }
  }, [currentBatch]);

  useEffect(() => {
    let readyToShipError = null;
    if (readyToShip) {
      const d = new Date(readyToShip);
      const today = dayjs().startOf("day");
      if (dayjs(d).isBefore(today)) {
        readyToShipError = "Ready to ship date must be in the future.";
      }
    }
    setReadyToShipDateErrorMessage(readyToShipError);
  }, [readyToShip]);

  const shipmentOptions = selectedPlacementOption?.shipmentOptions ?? [];

  return (
    <PageContent
      className={styles.pageContent}
      header={
        <PageHeader
          step={currentBatch.shipmentType === TransportType.SPD ? 2 : 1}
          shipmentType={currentBatch.shipmentType}
          continueButton={continueButton}
          onContinueClicked={onContinueClicked}
          onCancelClicked={onCancelClicked}
        />
      }
    >
      <div>
        <div className={`${styles.sectionContainer} ${styles.sectionContent}`}>
          <div className={styles.row}>
            <div className="strong">Select Inbound Placement Option</div>
            <div className="normal">({currentBatch.placementOptions.length} Total Options)</div>
            <span className={styles.spacer} />
            <Tooltip title={placementOptionNotice}>
              <span className={styles.icon}><IconInfo /></span>
            </Tooltip>
          </div>
          {currentBatch.shipmentType === TransportType.SPD && <div className={styles.row}>
            <Tooltip
              placement="bottom-right"
              title={readyToShipNotice}
            >
              <span className={styles.icon}><IconInfo /></span>
            </Tooltip>
            <label style={{minWidth:"max-content"}}>Ready to Ship:</label>
            <DatePicker
              className={styles.datePicker}
              invalid={!!readyToShipDateErrorMessage}
              value={new Date(readyToShip)}
              onChange={v => {
                setReadyToShip(v.toISOString());
                setConfirmUpdateReadyToShipDateModalOpen(true);
              }}
            />
            <span className={styles.spacer} />
          </div>}
        </div>
        {currentBatch.placementOptions.map(po => (
          <PlacementOption
            key={po.placementOptionId}
            placementOption={po}
            selectedPlacementOptionId={currentBatch?.selectedPlacementOptionId}
            setSelectedPlacementOptionId={id => {
              setSelectedShipmentTabIndex(0); // if the index is say, 3, and we switch to a tab with less than 4 shipments, we crash without this
              currentBatch.selectedPlacementOptionId = id;
              setCurrentBatch({...currentBatch});
            }}
          />
        ))}
      </div>
      <div>
        <Flex justify="end" gap={2}>
          <Button type="secondary" size="small" onClick={cloneShipment}>
            <IconCopyClipboard />
            Clone Shipment
          </Button>
          <Button
            type="secondary"
            size="small"
            onClick={() => {
              const placementOption = currentBatch.placementOptions.find(p => p.placementOptionId === currentBatch.selectedPlacementOptionId);
              if (!placementOption) return;
              const items: LabelsModalItem[] = [];
              for (const shipmentOption of placementOption.shipmentOptions) {
                for (const item of shipmentOption.items) {
                  const batchItem = currentBatch.items.find(i => i.sku === item.msku);
                  items.push({
                    batchItem,
                    fnsku: item.fnsku,
                    totalQuantity: item.quantity,
                    assignedQuantity: item.quantity,
                  });
                }
              }
              setLabelsModalItems(items);
            }}
          >
            <IconPrintGoogle />
            Print
          </Button>
        </Flex>
        <div className={styles.sectionContainer}>
          <Tabs
            activeTab={selectedShipmentTabIndex}
            setActiveTab={i => setSelectedShipmentTabIndex(i as number)}
            tabItemClassName={styles.shipmentTabItem}
            tabs={shipmentOptions.map((so, i) => ({
              key: i,
              label: <span style={{whiteSpace: "nowrap"}}>{selectedShipmentTabIndex === i ? `Shipment #${i + 1}` : `${i + 1}`}</span>,
              children: <ShipmentOption shipmentOption={so}/>,
            }))}
          />
        </div>
        {currentBatch?.shipmentType === TransportType.SPD && (
          <div className={styles.sectionContainer}>
            <div className={`${styles.sectionContent} ${styles.continueHeader}`}>
              <h4>Ready to continue?</h4>
              <div className={`weak`}>
                Take a moment to review the fee estimates, and check that all is correct.
              </div>
            </div>
            <div className={`${styles.sectionContent} ${styles.grid}`}>
              <div className={`weak`}>Total prep and labeling fees:</div>
              <div className={`${styles.right} strong`}>{formatValueAsCurrency(currentBatch.fees[currentBatch?.selectedPlacementOptionId]?.prep)} USD</div>
              <div className={`weak`}>Total placement fees:</div>
              <div className={`${styles.right} strong`}>
                {optPlacement && <span className="inline-positive"><IconCheckCircle />&nbsp;Optimized Placement</span>}
                <span className={optPlacement ? "positive" : "strong"}>
                  &nbsp;{formatValueAsCurrency(currentBatch.fees[currentBatch?.selectedPlacementOptionId]?.placement)} USD
                </span>
              </div>
              <div className={`weak`}>Total estimated shipping fees:</div>
              <div className={`${styles.right} strong`}>{formatValueAsCurrency(currentBatch.fees[currentBatch?.selectedPlacementOptionId]?.transportation)} USD</div>
            </div>
            <div className={`${styles.sectionContent} ${styles.row} ${styles.totalFees} ${styles.info}`}>
              <Tooltip title={"Total estimated prep, labeling, placement, and shipping fees (other fees may apply)"}>
                <span className={styles.icon}><IconInfo /></span>
              </Tooltip>
              <span className={styles.continueFees}>Total fees:</span>
              <span className={styles.spacer} />
              <span className="small-heading strong">{formatValueAsCurrency(currentBatch.fees[currentBatch?.selectedPlacementOptionId]?.total)} USD</span>
            </div>
          </div>
        )}
      </div>
      <ConfirmModal
        open={confirmUpdateReadyToShipDateModalOpen}
        title={"Confirm Change to Ready-to-Ship Date"}
        body={
          <>
            <p>
              Changing your shipment's ready-to-ship date will cause the transportation fees to be re-calculated and can take a few minutes.
            </p>
            <p>
              You're changing the ship date to: <b>{new Date(readyToShip).toLocaleDateString()}</b>
            </p>
          </>
        }
        onClose={async confirmed => {
          if (confirmed) {
            // patch the batch including recalcing transportation options
            const result = await updateBatchV2(currentBatch.id, { readyToShip });
            setCurrentBatch({ ...currentBatch, ...result } as FbaBatch);
          } else {
            setReadyToShip(currentBatch.readyToShip); // if we declined to set it, revert the date
          }
          setConfirmUpdateReadyToShipDateModalOpen(false);
        }}
      />
      <LabelsModal
        open={labelsModalItems?.length > 0}
        onClose={() => setLabelsModalItems([])}
        items={labelsModalItems ?? []}
      />
    </PageContent>
  );
}
